import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon, Intent, Tag } from '@blueprintjs/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { UserAccess, UserHasAccessActions } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { Breadcrumbs } from 'components/navigation';
import { Wizard } from 'components/wizard';
import { ArrayToEllipsesString } from 'components/formatting';
import { FormSelect, FormFileInput } from 'components/form-fields';
import { Table } from 'components/listing';
import { Tooltip } from 'components/tooltip';
import { ShowHide } from 'components/layout';
import { Button, LinkButton } from 'components/buttons';
import { NotificationToaster, NotificationInline, AlertUnsavedData } from 'components/notifications';
import { useDirtyData } from 'hooks/useDirtyData';
import { BulkImportService, BulkImportMappingService } from 'services';
import { GridLayout } from 'components/grid';
import { redirect } from 'utils/redirect';
import { ActionPanel } from 'components/panel';

import { canManageGlobalMappings, importTypes, importUploadActions } from './bulkImportTypes';
import './BulkImportUpload.css';
import { SaveMappingModal } from './SaveMappingModal';
import { BulkImportValidValuesModal } from './BulkImportValidValuesModal';

const defaultArray = [];

const importListRoute = '/import/bulk';

const badRequestCode = 400;
const uploadErrorMessage = "Could not upload the file. Please try again.";
const uploadSuccessMessage = "File uploaded successfully.";
const uploadDetailsErrorMessage = "Could not load the file columns. Please try again.";
const typeColumnsErrorMessage = "Could not load the import fields. Please try again.";
const mappingErrorMessage = "Could not submit the column mapping. Please try again.";
const savedMappingsErrorMessage = "Could not load the saved mappings. Please try again.";

const acceptedFileTypes = ['.xlsx'];
const fileHelperText = 'Accepted format: .xlsx — max 10 MB, 50,000 rows';

const noSelection = '';
const noSample = '-';

const stepUpload = 0;
const stepMap = 1;

const wizardStepInformation = [
    { stepTitle: 'Choose type and upload' },
    { stepTitle: 'Map columns' }
];

const selectSourcePlaceholder = { id: '', name: '- Select column -' };
const noSavedMappingItem = { id: '', name: '- No saved mapping -' };

const validValuesTooltip = 'View valid values';
const noValidValuesTooltip = 'Values are based on your Vision data';

// The mapping-step tooltip shows at most this many valid values, with a trailing ellipsis line
// when more exist — the same cap and overflow marker the bulk import error file uses. The
// reference table's modal deliberately has no cap.
const tooltipValuesCap = 15;

function normaliseForMatch(name) {
    if (name == null) {
        return null;
    }
    return name.replace(/[^\p{L}\p{N}]+/gu, '').toLowerCase();
}

function namesMatch(sourceName, friendlyName) {
    const normalisedSource = normaliseForMatch(sourceName);
    const normalisedFriendly = normaliseForMatch(friendlyName);
    return normalisedSource != null
        && normalisedFriendly != null
        && normalisedSource.length > 0
        && normalisedSource === normalisedFriendly;
}

const mappingHeaderData = {
    headers: [
        { key: 'field', value: 'FIELD' },
        { key: 'required', value: 'REQUIRED' },
        { key: 'mappedFrom', value: 'MAPPED FROM' },
        { key: 'sample', value: 'SAMPLE' }
    ]
};

const typeFieldsHeaderData = {
    headers: [
        { key: 'field', value: 'FIELD' },
        { key: 'required', value: 'REQUIRED' },
        { key: 'dataType', value: 'DATA TYPE' },
        { key: 'maxLength', value: 'MAX LENGTH' },
        { key: 'description', value: 'DESCRIPTION' },
        { key: 'validValues', value: 'VALID VALUES' }
    ]
};

function hasLookupValues(column) {
    return Array.isArray(column.lookupValues) && column.lookupValues.length > 0;
}

// The API returns the raw ImportColumnDataType enum name; these are the user-facing labels for
// the import fields reference table. An unrecognised type falls back to the raw name so a newly
// added backend type stays legible rather than rendering blank.
const dataTypeLabels = {
    String: 'Text',
    Lookup: 'Lookup',
    Date: 'Date',
    Bool: 'Yes/No (True/False)'
};

// The backend enum has no Number member — whole-number import fields arrive as String with an
// integer regex and no length cap, so a String column with no max length is a number field.
function resolveDataTypeLabel(column) {
    if (column.dataType === 'String' && column.maxLength == null) {
        return 'Number';
    }

    return dataTypeLabels[column.dataType] ?? column.dataType;
}

export function BulkImportUpload({ breadcrumbs = defaultArray }) {

    const userActions = useSelector((state) => state.loggedInUser.actions);
    const userRole = useSelector((state) => state.loggedInUser.role);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const resumeUploadIdParam = searchParams.get('uploadId');
    const resumeUploadId = resumeUploadIdParam != null && /^\d+$/.test(resumeUploadIdParam)
        ? Number(resumeUploadIdParam)
        : null;
    const resumeImportTypeKey = searchParams.get('importType');
    const resumeType = resumeImportTypeKey != null
        ? importTypes.find((type) => type.bulkImportTypeKey === resumeImportTypeKey) ?? null
        : null;
    const initialStep = resumeUploadId != null && resumeType != null ? stepMap : stepUpload;

    const [currentStep, setCurrentStep] = useState(initialStep);

    const [selectedTypeName, setSelectedTypeName] = useState(resumeType?.typeName ?? null);
    const [files, setFiles] = useState(defaultArray);

    const [uploadId, setUploadId] = useState(resumeUploadId);
    const [uploadDetails, setUploadDetails] = useState(null);
    const [targetColumns, setTargetColumns] = useState(defaultArray);
    const [columnMappings, setColumnMappings] = useState({});
    const [loadingMappingData, setLoadingMappingData] = useState(false);

    const [saving, setSaving] = useState(false);
    const [autoMatchApplied, setAutoMatchApplied] = useState(false);
    const [isQueued, setIsQueued] = useState(false);
    const [leaving, setLeaving] = useState(false);

    const [savedMappings, setSavedMappings] = useState(defaultArray);
    const [loadingSavedMappings, setLoadingSavedMappings] = useState(false);
    const [appliedMappingId, setAppliedMappingId] = useState(null);
    const [skippedPlanColumns, setSkippedPlanColumns] = useState(defaultArray);
    const [showSaveMappingModal, setShowSaveMappingModal] = useState(false);
    const [validValuesColumn, setValidValuesColumn] = useState(null);

    // The upload step persists nothing, so leaving it discards no work. Only an upload awaiting
    // mapping does, because the import already exists and abandoning it loses it. Cancelling is a
    // deliberate exit, so it must clear the flag rather than strand it in the store for the next
    // page, which has no dialog to release it.
    const dirtyBaselineRef = useRef({ hasUnfinishedUpload: false });
    const dirtyBaseline = dirtyBaselineRef.current;

    const typeColumnsRequestRef = useRef(null);

    const formState = { hasUnfinishedUpload: uploadId != null && !isQueued && !leaving };
    const isDataDirty = useDirtyData(dirtyBaseline, formState);

    useEffect(() => {
        if (leaving) {
            navigate(importListRoute);
        }
    }, [leaving]);

    useEffect(() => {
        if (currentStep === stepMap && uploadId != null) {
            loadMappingData(uploadId);
        }
    }, [currentStep, uploadId]);

    const selectedType = importTypes.find((type) => type.typeName === selectedTypeName) ?? null;
    const isLegacySelected = selectedType?.isLegacy ?? false;

    const availableTypeItems = importTypes
        .filter((type) => UserHasAccessActions([type.requiredClaim], userActions))
        .map((type) => {
            return { id: type.typeName, name: type.typeName };
        });

    const canProceedStepOne = selectedType != null && (isLegacySelected || files.length > 0);

    // A required column must always be mapped. A type can offer several match keys, any one of
    // which identifies the record, so one mapped match key is enough.
    function isColumnMapped(column) {
        return columnMappings[column.propertyName] !== noSelection && columnMappings[column.propertyName] != null;
    }

    const requiredMapped = targetColumns.filter((column) => column.isRequired).every(isColumnMapped);
    const matchKeyColumns = targetColumns.filter((column) => column.isMatchKey);
    const allMandatoryMapped = requiredMapped && (matchKeyColumns.length === 0 || matchKeyColumns.some(isColumnMapped));

    let canProceed = false;
    if (currentStep === stepUpload) {
        canProceed = canProceedStepOne;
    } else if (currentStep === stepMap) {
        canProceed = targetColumns.length > 0 && allMandatoryMapped;
    }

    const sourceColumns = uploadDetails?.sourceColumns ?? defaultArray;

    const chosenSourceNames = new Set(
        Object.values(columnMappings).filter((name) => {
            return name != null && name !== noSelection;
        })
    );

    const autoMatchCount = targetColumns.filter((targetColumn) => {
        return sourceColumns.some((sourceColumn) => {
            return namesMatch(sourceColumn.columnName, targetColumn.friendlyName);
        });
    }).length;

    const canManageGlobal = canManageGlobalMappings(userRole);

    const savedMappingItems = [noSavedMappingItem].concat(
        savedMappings.map((mapping) => {
            return { id: mapping.bulkImportMappingId, name: mapping.mappingName };
        })
    );

    const appliedMapping = savedMappings.find((mapping) => mapping.bulkImportMappingId === appliedMappingId) ?? null;

    // The current grid state as saveable columns — one entry per target field with a source
    // column selected. This seeds the create / update mapping dialog.
    const currentMappingColumns = targetColumns
        .filter((column) => {
            const sourceColumnName = columnMappings[column.propertyName];
            return sourceColumnName != null && sourceColumnName !== noSelection;
        })
        .map((column) => {
            return {
                propertyName: column.propertyName,
                friendlyName: column.friendlyName,
                sourceColumnName: columnMappings[column.propertyName]
            };
        });

    const skippedColumnsText = `The saved mapping references file columns that could not be applied and were skipped: ${skippedPlanColumns.join(', ')}.`;

    // The panel leads on auto-set while that prompt is available; once the columns are
    // auto-set (or a saved mapping takes over) the saved-mapping controls are all that
    // remain, so the title follows.
    const showAutoSetPrompt = appliedMappingId == null && autoMatchCount > 0 && !autoMatchApplied;

    let assistancePanelTitle = "Auto Set Columns?";
    if (!showAutoSetPrompt) {
        assistancePanelTitle = "Saved Mappings";
    }

    // Defined once so the with-tooltip (no mappings yet) and plain renderings below cannot
    // drift apart; the select is disabled until there is a mapping to apply.
    const hasSavedMappings = savedMappings.length > 0;
    const savedMappingSelect = (
        <FormSelect
            id="bulk-import-saved-mapping-select"
            headingText="Saved mapping"
            placeholder="Apply a saved mapping"
            items={savedMappingItems}
            selectedValue={appliedMappingId}
            onItemSelect={onSavedMappingSelect}
            loading={loadingSavedMappings}
            disabled={saving || !hasSavedMappings}
        />
    );

    function getSourceColumnItems(propertyName) {
        const currentSelection = columnMappings[propertyName];
        return [selectSourcePlaceholder].concat(
            sourceColumns
                .filter((sourceColumn) => {
                    return sourceColumn.columnName === currentSelection
                        || !chosenSourceNames.has(sourceColumn.columnName);
                })
                .map((sourceColumn) => {
                    return { id: sourceColumn.columnName, name: sourceColumn.columnName };
                })
        );
    }

    function onStepChange(step) {
        setCurrentStep(step);
    }

    function onTypeSelect(item) {
        setSelectedTypeName(item.id);
        setFiles(defaultArray);
        loadTypeColumns(item.id);
    }

    // The chosen type's fields are shown at step one so the user can check their file before
    // uploading it, and the same fetched columns are reused for the mapping grid at step two.
    // Legacy types are handled by the old wizard, so there are no fields to describe.
    function loadTypeColumns(typeName) {
        const type = importTypes.find((importType) => importType.typeName === typeName) ?? null;

        typeColumnsRequestRef.current = typeName;
        setTargetColumns(defaultArray);

        if (type == null || type.isLegacy) {
            return;
        }

        BulkImportService.getTypeColumns(type.microserviceKey, type.bulkImportTypeKey).then(
            (columns) => {
                // A slower response for a type the user has already moved off must not
                // replace the fields of the type now on screen.
                if (typeColumnsRequestRef.current !== typeName) {
                    return;
                }
                setTargetColumns(columns ?? defaultArray);
            },
            () => {
                if (typeColumnsRequestRef.current !== typeName) {
                    return;
                }
                NotificationToaster.show(Intent.DANGER, typeColumnsErrorMessage);
            }
        );
    }

    function onFileInputChange(e) {
        const list = e?.target?.files;
        if (list != null && list.length > 0) {
            setFiles([list[0]]);
        }
    }

    function onCancel() {
        dispatch({ type: 'RESET_DIRTY_FLAG' });
        setLeaving(true);
    }

    function redirectToLegacyImport() {
        redirect(`${window.env.VISION_URL}/#/Import?type=${encodeURIComponent(selectedType.legacyTypeId)}`);
    }

    function onBeforeStepChange(step) {
        if (step === stepMap) {
            return uploadSelectedFile();
        }

        return Promise.resolve();
    }

    function uploadSelectedFile() {
        if (isLegacySelected) {
            redirectToLegacyImport();
            return Promise.reject();
        }

        setSaving(true);

        return new Promise((resolve, reject) => {
            BulkImportService.uploadFile(selectedType.microserviceKey, files[0], selectedType.bulkImportTypeKey).then(
                (newUploadId) => {
                    setUploadId(newUploadId);
                    NotificationToaster.show(Intent.SUCCESS, uploadSuccessMessage);
                    setSaving(false);
                    resolve();
                },
                (error) => {
                    if (error != null && error.status === badRequestCode) {
                        NotificationToaster.show(Intent.WARNING, error.data);
                    } else {
                        NotificationToaster.show(Intent.DANGER, uploadErrorMessage);
                    }
                    setSaving(false);
                    reject();
                }
            );
        });
    }

    function loadMappingData(currentUploadId) {
        setLoadingMappingData(true);
        setAppliedMappingId(null);
        setSkippedPlanColumns(defaultArray);
        loadSavedMappings();

        BulkImportService.getUpload(selectedType.microserviceKey, currentUploadId).then(
            (upload) => {
                // Step one has already fetched the columns for the chosen type; the refetch is
                // only needed when the wizard resumed straight into the mapping step.
                if (targetColumns.length > 0) {
                    applyMappingData(upload, targetColumns);
                    return;
                }

                BulkImportService.getTypeColumns(selectedType.microserviceKey, selectedType.bulkImportTypeKey).then(
                    (columns) => {
                        applyMappingData(upload, columns ?? defaultArray);
                    },
                    () => {
                        NotificationToaster.show(Intent.DANGER, typeColumnsErrorMessage);
                        setLoadingMappingData(false);
                    }
                );
            },
            () => {
                // The mapping grid maps target fields onto the uploaded file's columns, so
                // without the file's columns there is nothing to map from — the fields fetched
                // for the reference table at step one are cleared to keep the grid hidden.
                NotificationToaster.show(Intent.DANGER, uploadDetailsErrorMessage);
                setTargetColumns(defaultArray);
                setLoadingMappingData(false);
            }
        );
    }

    function applyMappingData(upload, columns) {
        setUploadDetails(upload);
        setTargetColumns(columns);

        const initialMappings = {};
        columns.forEach((column) => {
            initialMappings[column.propertyName] = noSelection;
        });

        setColumnMappings(initialMappings);
        setAutoMatchApplied(false);

        setLoadingMappingData(false);
    }

    function loadSavedMappings() {
        setLoadingSavedMappings(true);

        BulkImportMappingService.getMappingsByType(selectedType.microserviceKey, selectedType.bulkImportTypeKey).then(
            (mappings) => {
                setSavedMappings(mappings ?? defaultArray);
                setLoadingSavedMappings(false);
            },
            () => {
                NotificationToaster.show(Intent.DANGER, savedMappingsErrorMessage);
                setSavedMappings(defaultArray);
                setLoadingSavedMappings(false);
            }
        );
    }

    function onSavedMappingSelect(item) {
        if (item.id === noSelection) {
            // Unapplying returns the panel to create mode and clears the applied plan from
            // the grid, so every field goes back to unmapped (which also lets the auto-set
            // prompt reappear when headers match).
            const clearedMappings = {};
            targetColumns.forEach((column) => {
                clearedMappings[column.propertyName] = noSelection;
            });
            setColumnMappings(clearedMappings);
            setAppliedMappingId(null);
            setSkippedPlanColumns(defaultArray);
            return;
        }

        const mapping = savedMappings.find((savedMapping) => savedMapping.bulkImportMappingId === item.id);
        if (mapping == null) {
            return;
        }

        applySavedMapping(mapping);
    }

    function applySavedMapping(mapping) {
        // Applying pre-fills every field from the mapping, so fields the plan doesn't cover
        // reset to unmapped rather than keeping any earlier manual selection.
        const nextMappings = {};
        targetColumns.forEach((column) => {
            nextMappings[column.propertyName] = noSelection;
        });

        // Plan entries are keyed by file column name; an entry only applies when its file
        // column exists in this upload and its target field is still part of the import
        // type. Stale entries are skipped and surfaced as a note rather than blocking.
        const skippedColumns = [];
        (mapping.mappingPlan ?? defaultArray).forEach((planEntry) => {
            const sourceExists = sourceColumns.some((sourceColumn) => sourceColumn.columnName === planEntry.key);
            const targetExists = targetColumns.some((targetColumn) => targetColumn.propertyName === planEntry.value);
            if (!sourceExists || !targetExists) {
                skippedColumns.push(planEntry.key);
                return;
            }
            nextMappings[planEntry.value] = planEntry.key;
        });

        setColumnMappings(nextMappings);
        setSkippedPlanColumns(skippedColumns);
        setAppliedMappingId(mapping.bulkImportMappingId);
    }

    function onMappingSaved(savedMappingId) {
        // The grid already reflects what was saved, so only the applied selection moves to
        // the saved mapping — the list refresh brings its latest name and scope in.
        setShowSaveMappingModal(false);
        setAppliedMappingId(savedMappingId);
        setSkippedPlanColumns(defaultArray);
        loadSavedMappings();
    }

    function onMappingChange(propertyName, item) {
        setColumnMappings((previousMappings) => {
            return {
                ...previousMappings,
                [propertyName]: item.id
            };
        });
    }

    function applyAutoMatch() {
        setColumnMappings((previousMappings) => {
            const nextMappings = { ...previousMappings };
            const usedSourceNames = new Set(
                Object.values(nextMappings).filter((name) => {
                    return name != null && name !== noSelection;
                })
            );

            targetColumns.forEach((targetColumn) => {
                const existing = nextMappings[targetColumn.propertyName];
                if (existing != null && existing !== noSelection) {
                    return;
                }
                const match = sourceColumns.find((sourceColumn) => {
                    return namesMatch(sourceColumn.columnName, targetColumn.friendlyName);
                });
                if (match == null || usedSourceNames.has(match.columnName)) {
                    return;
                }
                nextMappings[targetColumn.propertyName] = match.columnName;
                usedSourceNames.add(match.columnName);
            });

            return nextMappings;
        });
        setAutoMatchApplied(true);
    }

    function buildMappingPlan() {
        const mappingPlan = {};
        Object.entries(columnMappings).forEach(([propertyName, sourceColumnName]) => {
            if (sourceColumnName !== noSelection) {
                mappingPlan[sourceColumnName] = propertyName;
            }
        });
        return mappingPlan;
    }

    function submitColumnMapping() {
        setSaving(true);

        return new Promise((resolve, reject) => {
            BulkImportService.submitMapping(selectedType.microserviceKey, uploadId, buildMappingPlan()).then(
                () => {
                    setSaving(false);
                    setIsQueued(true);
                    resolve();
                },
                (error) => {
                    if (error?.status === badRequestCode) {
                        NotificationToaster.show(Intent.WARNING, error.data);
                    } else {
                        NotificationToaster.show(Intent.DANGER, mappingErrorMessage);
                    }
                    setSaving(false);
                    reject();
                }
            );
        });
    }

    function onImportAnother() {
        setIsQueued(false);
        setCurrentStep(stepUpload);
        setSelectedTypeName(null);
        setFiles(defaultArray);
        setUploadId(null);
        setUploadDetails(null);
        typeColumnsRequestRef.current = null;
        setTargetColumns(defaultArray);
        setColumnMappings({});
        setAutoMatchApplied(false);
        setSavedMappings(defaultArray);
        setAppliedMappingId(null);
        setSkippedPlanColumns(defaultArray);
        setShowSaveMappingModal(false);
    }

    function getSelectedSampleData(propertyName) {
        const selectedSourceName = columnMappings[propertyName];
        if (selectedSourceName == null || selectedSourceName === noSelection) {
            return noSample;
        }

        const sourceColumn = sourceColumns.find((column) => column.columnName === selectedSourceName);
        if (sourceColumn == null || sourceColumn.sampleData == null) {
            return noSample;
        }

        return sourceColumn.sampleData;
    }

    function buildFieldTooltipContent(column) {
        if (!hasLookupValues(column)) {
            return column.description;
        }

        // The description, then the column's valid values one per line, capped with a trailing
        // ellipsis line when more exist. The reference table's modal is the uncapped surface.
        return (
            <div>
                <div>{column.description}</div>
                <div>Valid values:</div>
                <ArrayToEllipsesString length={tooltipValuesCap} multiline>
                    {column.lookupValues}
                </ArrayToEllipsesString>
            </div>
        );
    }

    function buildFieldCell(column) {
        return (
            <div className="inline-items">
                <span>{column.friendlyName}</span>
                <Tooltip content={buildFieldTooltipContent(column)} position="top">
                    <Icon icon="info-sign" size={14} />
                </Tooltip>
            </div>
        );
    }

    // Every Lookup field gets the control; one with no values available (e.g. Base Location,
    // whose account can hold too many locations to usefully list) shows it disabled with a
    // pointer to Vision instead. Non-Lookup fields have no values to show, so their cell is empty.
    function buildValidValuesCell(column) {
        if (column.dataType !== 'Lookup') {
            return null;
        }

        const button = (
            <Button
                icon="list"
                iconOnly={true}
                minimal={true}
                large={false}
                text={validValuesTooltip}
                id={`bulk-import-valid-values-${column.propertyName}`}
                onClick={() => setValidValuesColumn(column)}
                disabled={!hasLookupValues(column)}
            />
        );

        let tooltipText = validValuesTooltip;
        if (!hasLookupValues(column)) {
            tooltipText = noValidValuesTooltip;
        }

        return (
            <Tooltip content={tooltipText}>
                {button}
            </Tooltip>
        );
    }

    function buildRequiredCell(column) {
        let requiredCell = null;
        if (column.isConditional) {
            requiredCell = (<span className="text-intent-warning">Conditional</span>);
        } else if (column.isRequired) {
            requiredCell = (<span className="text-intent-danger">Required</span>);
        } else {
            requiredCell = (<span className="bp6-text-muted">Optional</span>);
        }
        return requiredCell;
    }

    function buildMappedFromCell(column) {
        const select = (
            <FormSelect
                id={`bulk-import-mapping-${column.propertyName}`}
                items={getSourceColumnItems(column.propertyName)}
                selectedValue={columnMappings[column.propertyName] ?? noSelection}
                onItemSelect={(item) => onMappingChange(column.propertyName, item)}
                disabled={saving}
            />
        );

        return (
            <span className="inline-items">
                {select}
                <ShowHide
                    evaluator={column.isMatchKey}
                    show={(<Tag intent={Intent.PRIMARY} className="bulk-import-match-key">Match key</Tag>)}
                />
            </span>
        );
    }

    const mappingRows = targetColumns.map((column) => {
        return {
            key: column.propertyName,
            cells: [
                buildFieldCell(column),
                buildRequiredCell(column),
                buildMappedFromCell(column),
                getSelectedSampleData(column.propertyName)
            ]
        };
    });

    const typeFieldRows = targetColumns.map((column) => {
        return {
            key: `type-field-${column.propertyName}`,
            cells: [
                <div className="inline-items inline-items-space" key={column.propertyName}>
                    {column.friendlyName}
                    <ShowHide
                        evaluator={column.isMatchKey}
                        show={(<Tag intent={Intent.PRIMARY} className="bulk-import-match-key">Match key</Tag>)}
                    />
                </div>,
                buildRequiredCell(column),
                resolveDataTypeLabel(column),
                column.maxLength ?? noSample,
                column.description,
                buildValidValuesCell(column)
            ]
        };
    });

    return (
        <UserAccess
            perform={importUploadActions}
            yes={() => (
                <ShowHide
                    evaluator={isQueued}
                    show={(
                        <div className="row">
                            <Breadcrumbs items={breadcrumbs} />

                            <div className="spacer-top">
                                <div className="spacer-bottom-medium">
                                    <Icon icon="tick-circle" size={40} intent={Intent.SUCCESS} />
                                </div>
                                <h2>Import queued</h2>
                                <p>Your import has been queued for processing. You can track its progress in the imports list.</p>
                                <div className="button-row spacer-top">
                                    <LinkButton
                                        intent="primary"
                                        text="View all imports"
                                        id="bulk-import-view-all"
                                        href={importListRoute}
                                    />
                                    <Button
                                        intent="primary"
                                        text="Import another file"
                                        id="bulk-import-another"
                                        onClick={onImportAnother}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    hide={(
                        <div className="row">
                            <Breadcrumbs items={breadcrumbs} />

                            <h1>New Import</h1>

                            <Wizard
                                id="bulk-import-upload-wizard"
                                onStepChange={onStepChange}
                                onFinish={submitColumnMapping}
                                initialStep={initialStep}
                                canProceed={canProceed}
                                canFinish={currentStep === stepMap}
                                finishButtonText="Start import"
                                disabled={saving}
                                loading={loadingMappingData}
                                saveBetweenSteps
                                alwaysSaveBetweenSteps
                                showSaveButton={false}
                                saveAction={onBeforeStepChange}
                                additionalStepInformation={wizardStepInformation}
                                additionalButtonsStart={(
                                    <Button
                                        intent="secondary"
                                        text="Cancel"
                                        id="bulk-import-cancel"
                                        onClick={onCancel}
                                        loading={loadingMappingData}
                                        disabled={saving}
                                    />
                                )}
                                allowBack={false}
                            >
                                <div>
                                    <GridLayout numberOfColumns={2}>
                                        <div>
                                            <FormSelect
                                                id="bulk-import-type-select"
                                                headingText="Import type"
                                                placeholder="Choose an import type"
                                                items={availableTypeItems}
                                                selectedValue={selectedTypeName}
                                                onItemSelect={onTypeSelect}
                                                loading={loadingMappingData}
                                                disabled={saving}
                                            />

                                            <ShowHide
                                                evaluator={selectedType != null && !isLegacySelected}
                                                show={(
                                                    <FormFileInput
                                                        id="bulk-import-file-uploader"
                                                        headingText="Upload file"
                                                        buttonText="Browse"
                                                        helperText={fileHelperText}
                                                        acceptedMimeTypes={acceptedFileTypes}
                                                        text={files.length > 0 ? files[0].name : undefined}
                                                        hasSelection={files.length > 0}
                                                        onChange={onFileInputChange}
                                                        disabled={saving}
                                                        loading={loadingMappingData}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </GridLayout>

                                    <ShowHide
                                        evaluator={isLegacySelected}
                                        show={(
                                            <p className="spacer-bottom">
                                                This import type is handled in the legacy import wizard. Continue to be taken there.
                                            </p>
                                        )}
                                    />

                                    <ShowHide
                                        evaluator={!isLegacySelected && targetColumns.length > 0}
                                        show={(
                                            <div className="spacer-bottom">
                                                <h2>Import fields</h2>
                                                <div id="bulk-import-type-fields">
                                                    <Table
                                                        headerData={typeFieldsHeaderData}
                                                        data={typeFieldRows}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    />
                                </div>

                                <div className="spacer-bottom">

                                    <ShowHide
                                        evaluator={!loadingMappingData && targetColumns.length > 0}
                                        show={(
                                            <div className='spacer-bottom'>
                                                <ActionPanel title={assistancePanelTitle} intent='info' >
                                                    <ShowHide
                                                        evaluator={showAutoSetPrompt}
                                                        show={(
                                                            <div className="inline-items spacer-bottom-small">
                                                                <div className="inline-item fill">
                                                                    <p>We found {autoMatchCount} column header(s) from your file that match the mapping headers.</p>
                                                                </div>
                                                                <div className="inline-item">
                                                                    <Button
                                                                        intent="primary"
                                                                        icon="tick"
                                                                        text="Auto-set columns"
                                                                        id="bulk-import-auto-match"
                                                                        onClick={applyAutoMatch}
                                                                        disabled={saving}
                                                                        large={false}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    />
                                                    <div className="inline-items">
                                                        <div className="inline-item fill">
                                                            <ShowHide
                                                                evaluator={hasSavedMappings}
                                                                show={savedMappingSelect}
                                                                hide={(
                                                                    <Tooltip content="You haven't saved any mappings yet. Create a mapping to reuse it here.">
                                                                        {savedMappingSelect}
                                                                    </Tooltip>
                                                                )}
                                                            />
                                                        </div>
                                                        <div className="inline-item">
                                                            <ShowHide
                                                                evaluator={appliedMappingId == null}
                                                                show={(
                                                                    <Button
                                                                        intent="primary"
                                                                        icon="floppy-disk"
                                                                        text="Create mapping"
                                                                        id="bulk-import-create-mapping"
                                                                        onClick={() => setShowSaveMappingModal(true)}
                                                                        disabled={saving}
                                                                        large={false}
                                                                    />
                                                                )}
                                                                hide={(
                                                                    <Button
                                                                        intent="primary"
                                                                        icon="floppy-disk"
                                                                        text="Update mapping"
                                                                        id="bulk-import-update-mapping"
                                                                        onClick={() => setShowSaveMappingModal(true)}
                                                                        disabled={saving || appliedMapping == null}
                                                                        large={false}
                                                                    />
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                    <NotificationInline
                                                        intent="warning"
                                                        show={skippedPlanColumns.length > 0}
                                                        allowClose={false}
                                                        text={skippedColumnsText}
                                                    />
                                                </ActionPanel>
                                            </div>
                                        )}
                                    />

                                    <h2>Map columns</h2>

                                    <ShowHide
                                        evaluator={!loadingMappingData && targetColumns.length > 0}
                                        show={(
                                            <div>
                                                <NotificationInline
                                                    intent="info"
                                                    show
                                                    allowClose={false}
                                                    text={selectedType?.matchKeyHelperText}
                                                />

                                                <div id="bulk-import-mapping-list">
                                                    <Table
                                                        headerData={mappingHeaderData}
                                                        data={mappingRows}
                                                        loading={loadingMappingData}
                                                        saving={saving}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    />
                                </div>
                            </Wizard>

                            <SaveMappingModal
                                showModal={showSaveMappingModal}
                                appliedMapping={appliedMapping}
                                canManageGlobal={canManageGlobal}
                                microserviceKey={selectedType?.microserviceKey}
                                bulkImportTypeKey={selectedType?.bulkImportTypeKey}
                                initialColumns={currentMappingColumns}
                                onSaved={onMappingSaved}
                                onClose={() => setShowSaveMappingModal(false)}
                            />

                            <BulkImportValidValuesModal
                                showModal={validValuesColumn != null}
                                friendlyName={validValuesColumn?.friendlyName ?? ''}
                                values={validValuesColumn?.lookupValues ?? defaultArray}
                                onClose={() => setValidValuesColumn(null)}
                            />

                            <AlertUnsavedData isDirty={isDataDirty && !leaving} />
                        </div>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

BulkImportUpload.propTypes = {
    breadcrumbs: PropTypes.array
};
